/**
 * SpecPanel - the report builder's settings (docs/plans/crm-report-builder-search-collab-plan.md A4.2 to
 * A4.4, B4.3, B4.6; work package R-7): what to count, the measure, up to two groupings (a time split
 * counts as one of the two), the dates, the filters, and bars or a table.
 *
 * Every choice comes from builderLogic nextChoices, so a choice that would break a rule of plan B4.3
 * is shown DISABLED, never silently dropped: it stays in its place, dimmed with a dashed edge, its
 * reason is its tooltip, and tapping it puts the reason under its group (a phone has no hover). The
 * panel only reports changes (onChange with a BuilderChange, applied by builderLogic applyChoice);
 * the one thing it holds is the lead-source text while it is being typed, sent on Enter or when the
 * box is left. The catalog carries no list of lead sources and the CRM has no endpoint for one, so
 * that filter is free text (up to 200 characters, as the server takes it) with suggestions from the
 * lead sources the builder has seen in results.
 *
 * Below lg: the settings can fold away behind a one-line summary (specSummary) and a Change button,
 * hidden with the `hidden` class rather than unmounted, so a phone reaches the result without
 * scrolling past every group; the builder folds them when a saved report is opened. From lg: they are
 * always shown beside the result.
 */
import { useEffect, useId, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { X } from 'lucide-react';
import { FilterSelect, SegmentedControl } from '../components/filters';
import { Alert, chipCls, fieldCls } from '../components/ui';
import { studyInternalLabel } from '../types';
import type { StudyOption, TeamMember } from '../types';
import { FILTER_KEYS, RANGE_CHOICES, groupingOf, measureOf, nextChoices, subjectOf } from './builderLogic';
import type { BuilderChange, BuilderChoice, FilterChoice } from './builderLogic';
import type { ReportCatalog, ReportSpec } from './types';

/** The dates the server takes (crmReportBuilder/spec.js: custom dates between 2000 and 2099). */
export const DATE_MIN = '2000-01-01';
export const DATE_MAX = '2099-12-31';
/** A lead-source filter is text of at most 200 characters (spec.js). */
export const LEAD_SOURCE_MAX = 200;

export interface SpecPanelProps {
  spec: ReportSpec;
  catalog: ReportCatalog;
  onChange: (change: BuilderChange) => void;
  /** The network's studies (GET /api/crm/studies rows with an id), for the study filter. */
  studies: readonly StudyOption[];
  /** The team (lib useTeam), for the recruiter, teammate or recorder filter. */
  team: readonly TeamMember[];
  /** Lead sources seen in results, offered while typing the lead-source filter. */
  leadSourceSuggestions: readonly string[];
  /** While a saved report is opening: every control waits. */
  disabled?: boolean;
  /**
   * Below lg: the settings show (true, the default) or fold away behind a one-line summary and a
   * Change button, so a phone reaches the result without scrolling past them. From lg: always shown.
   */
  open?: boolean;
  /** Offers the fold (below lg) when given. */
  onToggleOpen?: () => void;
}

/** One line saying what the report counts: the folded panel's summary on a phone. */
export function specSummary(spec: ReportSpec, catalog: ReportCatalog): string {
  const subject = subjectOf(spec.subject, catalog);
  if (!subject) return 'Choose what to count.';
  const measure = measureOf(subject, spec.measure);
  const grouping = (key: string | null): string | null => (key ? (groupingOf(subject, key)?.label ?? key).toLowerCase() : null);
  let head = `${subject.label}: ${measure ? measure.label : spec.measure || 'choose a measure'}`;
  const rows = grouping(spec.rows);
  const columns = grouping(spec.columns);
  if (rows) head += ` by ${rows}`;
  if (columns) head += ` and ${columns}`;
  if (spec.time) head += `, per ${spec.time.grain}`;
  const parts = [head];
  if (spec.range.preset === 'custom') {
    parts.push(`${spec.range.from || '?'} to ${spec.range.to || '?'}`);
  } else {
    const range = RANGE_CHOICES.find((r) => r.key === spec.range.preset);
    if (range) parts.push(range.label);
  }
  const filters = FILTER_KEYS.filter((k) => !!spec.filters[k]).length;
  if (filters) parts.push(filters === 1 ? '1 filter' : `${filters} filters`);
  return parts.join('; ');
}

/** A choice's stable key (None is null). */
const choiceKey = (c: BuilderChoice): string => c.value ?? 'none';

/** Micro-label of a group (the design system's text-[10px] uppercase label). */
const labelCls = 'text-[10px] font-semibold uppercase tracking-widest text-gray-500 dark:text-gray-400';

// ---------------------------------------------------------------------------------------------
// A group of choices
// ---------------------------------------------------------------------------------------------

function ChoiceGroup({
  group,
  title,
  hint,
  aside,
  choices,
  onPick,
  children,
}: {
  group: string;
  title: string;
  hint?: string | null;
  aside?: ReactNode;
  choices: readonly BuilderChoice[];
  onPick: (change: BuilderChange) => void;
  children?: ReactNode;
}) {
  // The disabled choice last tapped: its reason shows under the group while it stays disabled.
  const [explained, setExplained] = useState<string | null>(null);
  const headingId = useId();
  const reasonId = useId();
  const tapped = explained === null ? null : choices.find((c) => choiceKey(c) === explained && c.disabled) ?? null;
  return (
    <div data-rb-group={group}>
      <div className="mb-1.5 flex items-baseline justify-between gap-2">
        <h3 id={headingId} className={labelCls}>{title}</h3>
        {aside}
      </div>
      {hint && <p className="-mt-1 mb-1.5 text-xs text-gray-500 dark:text-gray-400">{hint}</p>}
      <div role="group" aria-labelledby={headingId} className="flex flex-wrap gap-1.5">
        {choices.map((c) => {
          const key = choiceKey(c);
          return (
            <button
              key={key}
              type="button"
              data-rb-choice={key}
              aria-pressed={c.selected}
              aria-disabled={c.disabled ? true : undefined}
              aria-describedby={tapped && choiceKey(tapped) === key ? reasonId : undefined}
              title={c.reason ?? undefined}
              onClick={() => {
                if (c.disabled) {
                  setExplained(key);
                  return;
                }
                setExplained(null);
                if (!c.selected) onPick(c.change);
              }}
              className={`${chipCls(c.selected)} ${
                c.disabled ? 'cursor-not-allowed border-dashed text-gray-400 opacity-70 dark:text-gray-500' : ''
              }`}
            >
              {c.label}
            </button>
          );
        })}
      </div>
      {tapped && tapped.reason && (
        <p id={reasonId} role="status" data-rb-reason={group} className="mt-1.5 text-xs text-amber-700 dark:text-amber-300">
          {tapped.reason}
        </p>
      )}
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------------------------
// Filters
// ---------------------------------------------------------------------------------------------

/**
 * The lead-source box: free text, sent on Enter or when the box is left; the X clears it. The report
 * matches the recorded lead source exactly (the server keeps the text as sent), so what is typed is
 * trimmed, and the lead sources seen in results are offered as suggestions.
 */
function TextFilter({
  label,
  value,
  suggestions,
  onCommit,
}: {
  label: string;
  value: string | null;
  suggestions: readonly string[];
  onCommit: (value: string | null) => void;
}) {
  const [draft, setDraft] = useState(value ?? '');
  const listId = useId();
  const hintId = useId();
  // A new value from outside (a report opened, the filter cleared) replaces what was being typed.
  useEffect(() => {
    setDraft(value ?? '');
  }, [value]);
  const commit = () => {
    const next = draft.trim() || null;
    if (next !== (value ?? null)) onCommit(next);
    else setDraft(value ?? '');
  };
  return (
    <div>
      <div className="relative">
        <input
          type="text"
          value={draft}
          list={suggestions.length ? listId : undefined}
          maxLength={LEAD_SOURCE_MAX}
          placeholder="Any source"
          aria-label={label}
          aria-describedby={hintId}
          data-rb-text-filter="lead_source"
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commit();
            }
          }}
          className={`${fieldCls} min-h-[44px] w-full pr-12 sm:pr-9 sm:min-h-0`}
        />
        {value && (
          <button
            type="button"
            aria-label={`Clear ${label.toLowerCase()}`}
            onClick={() => {
              setDraft('');
              onCommit(null);
            }}
            className="absolute right-1 top-1/2 inline-flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-md text-gray-400 transition-colors duration-150 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:hover:bg-gray-600 dark:hover:text-gray-200 sm:h-7 sm:w-7"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        )}
      </div>
      {suggestions.length > 0 && (
        <datalist id={listId}>
          {suggestions.map((s) => (
            <option key={s} value={s} />
          ))}
        </datalist>
      )}
      <p id={hintId} className="mt-1 text-[11px] text-gray-500 dark:text-gray-400" data-rb="lead-source-hint">
        {suggestions.length > 0
          ? 'Matches the recorded lead source exactly.'
          : 'Matches the recorded lead source exactly. Group by lead source to see the ones in use.'}
      </p>
    </div>
  );
}

function FilterField({
  filter,
  studies,
  team,
  suggestions,
  onChange,
}: {
  filter: FilterChoice;
  studies: readonly StudyOption[];
  team: readonly TeamMember[];
  suggestions: readonly string[];
  onChange: (change: BuilderChange) => void;
}) {
  const set = (value: string | null) => onChange({ kind: 'filter', key: filter.key, value });
  const value = filter.value ?? '';
  let control: ReactNode;
  if (filter.options) {
    const known = !value || filter.options.some((o) => o.key === value);
    control = (
      <FilterSelect label={filter.label} value={value} onChange={(v) => set(v || null)}>
        <option value="">Any</option>
        {filter.options.map((o) => (
          <option key={o.key} value={o.key}>{o.label}</option>
        ))}
        {!known && <option value={value}>{value}</option>}
      </FilterSelect>
    );
  } else if (filter.key === 'study_id') {
    const known = !value || studies.some((s) => s.id === value);
    control = (
      <FilterSelect label={filter.label} value={value} onChange={(v) => set(v || null)}>
        <option value="">All studies</option>
        {studies.map((s) => (
          <option key={s.id as string} value={s.id as string}>{studyInternalLabel(s) || s.slug}</option>
        ))}
        {!known && <option value={value}>The chosen study</option>}
      </FilterSelect>
    );
  } else if (filter.key === 'owner') {
    const known = !value || team.some((m) => m.id === value);
    control = (
      <FilterSelect label={filter.label} value={value} onChange={(v) => set(v || null)}>
        <option value="">Everyone</option>
        {team.map((m) => (
          <option key={m.id} value={m.id}>{m.name}</option>
        ))}
        {!known && <option value={value}>The chosen teammate</option>}
      </FilterSelect>
    );
  } else {
    control = <TextFilter label={filter.label} value={filter.value} suggestions={suggestions} onCommit={set} />;
  }
  return (
    <div data-rb-filter={filter.key}>
      <span aria-hidden="true" className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">
        {filter.label}
      </span>
      {control}
    </div>
  );
}

// ---------------------------------------------------------------------------------------------
// The panel
// ---------------------------------------------------------------------------------------------

/** The report settings. Holds no report state: every change goes to onChange. */
export default function SpecPanel({
  spec,
  catalog,
  onChange,
  studies,
  team,
  leadSourceSuggestions,
  disabled = false,
  open = true,
  onToggleOpen,
}: SpecPanelProps) {
  const choices = nextChoices(spec, catalog);
  const subject = subjectOf(spec.subject, catalog);
  const headingId = useId();
  const bodyId = useId();
  const folded = !!onToggleOpen && !open;
  const sortedStudies = useMemo(
    () =>
      studies
        .filter((s) => !!s.id)
        .slice()
        .sort((a, b) => (studyInternalLabel(a) || a.slug).localeCompare(studyInternalLabel(b) || b.slug, undefined, { sensitivity: 'base' })),
    [studies],
  );
  const sortedTeam = useMemo(
    () => team.slice().sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })),
    [team],
  );
  const someDisabled = [choices.subject, choices.measure, choices.rows, choices.columns, choices.grain, choices.range]
    .some((list) => list.some((c) => c.disabled));
  const custom = spec.range.preset === 'custom';
  const dateCls = `${fieldCls} mt-1 block min-h-[44px] w-full sm:min-h-0`;

  return (
    <section
      aria-labelledby={headingId}
      data-rb="spec-panel"
      className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800"
    >
      <div className={`flex items-center justify-between gap-2 ${folded ? 'lg:mb-3' : 'mb-3'}`}>
        <h2 id={headingId} className="text-sm font-semibold text-gray-900 dark:text-gray-100">
          Report settings
        </h2>
        {onToggleOpen && (
          <button
            type="button"
            onClick={onToggleOpen}
            aria-expanded={open}
            aria-controls={bodyId}
            data-rb="settings-toggle"
            className="inline-flex min-h-[44px] items-center rounded-lg px-2.5 text-sm font-medium text-primary-700 transition-colors duration-150 hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-primary-400 dark:hover:bg-primary-900/20 sm:min-h-0 sm:py-1.5 lg:hidden"
          >
            {open ? 'Hide' : 'Change'}
          </button>
        )}
      </div>
      {folded && (
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-300 lg:hidden" data-rb="settings-summary">
          {specSummary(spec, catalog)}
        </p>
      )}
      <fieldset
        id={bodyId}
        disabled={disabled}
        data-rb="settings-body"
        className={`min-w-0 space-y-4 ${folded ? 'hidden lg:block' : ''}`}
      >
        <legend className="sr-only">Report settings</legend>
        {someDisabled && (
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Dimmed choices can&apos;t be combined with what is picked. Tap one to see why.
          </p>
        )}

        <ChoiceGroup group="subject" title="What to count" choices={choices.subject} onPick={onChange} />
        <ChoiceGroup group="measure" title="Measure" choices={choices.measure} onPick={onChange} />
        <ChoiceGroup
          group="rows"
          title="Group by"
          aside={(
            <span
              className="text-[11px] tabular-nums text-gray-500 dark:text-gray-400"
              title="A report splits two ways at most: a second grouping, a time split and a measure by stage each count as one."
              data-rb="axes"
            >
              {`${choices.axes.used} of ${choices.axes.max} ways used`}
            </span>
          )}
          choices={choices.rows}
          onPick={onChange}
        />
        <ChoiceGroup group="columns" title="Then by" choices={choices.columns} onPick={onChange} />
        <ChoiceGroup group="grain" title="Split by time" choices={choices.grain} onPick={onChange} />
        <ChoiceGroup
          group="range"
          title="Dates"
          hint={subject && subject.time_label ? `Counted by ${subject.time_label}.` : null}
          choices={choices.range}
          onPick={onChange}
        >
          {custom && (
            <div className="mt-2 grid grid-cols-2 gap-2" data-rb="custom-dates">
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-300">
                From
                <input
                  type="date"
                  min={DATE_MIN}
                  max={DATE_MAX}
                  value={spec.range.from ?? ''}
                  data-rb-date="from"
                  onChange={(e) => onChange({ kind: 'range', preset: 'custom', from: e.target.value || null })}
                  className={dateCls}
                />
              </label>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-300">
                To
                <input
                  type="date"
                  min={DATE_MIN}
                  max={DATE_MAX}
                  value={spec.range.to ?? ''}
                  data-rb-date="to"
                  onChange={(e) => onChange({ kind: 'range', preset: 'custom', to: e.target.value || null })}
                  className={dateCls}
                />
              </label>
            </div>
          )}
        </ChoiceGroup>

        {choices.filters.length > 0 && (
          <div data-rb-group="filters">
            <h3 className={`mb-1.5 ${labelCls}`}>Filters</h3>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-1">
              {choices.filters.map((f) => (
                <FilterField
                  key={f.key}
                  filter={f}
                  studies={sortedStudies}
                  team={sortedTeam}
                  suggestions={leadSourceSuggestions}
                  onChange={onChange}
                />
              ))}
            </div>
          </div>
        )}

        <div data-rb-group="chart" className="flex flex-wrap items-center justify-between gap-2">
          <h3 className={labelCls}>Show as</h3>
          <SegmentedControl
            label="Show as"
            value={spec.chart}
            onChange={(v) => onChange({ kind: 'chart', value: v === 'table' ? 'table' : 'bars' })}
            options={choices.chart.map((c) => ({ value: c.value ?? 'bars', label: c.label }))}
          />
        </div>

        {choices.problem && (
          <Alert tone="warning" size="sm">
            <span data-rb="panel-problem">{choices.problem}</span>
          </Alert>
        )}
      </fieldset>
    </section>
  );
}
